import { Box, Stack, Typography, useTheme } from "@mui/material";
import { AgGridEvent, ColDef } from "ag-grid-community";
import { Ref, forwardRef, lazy, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { isMobile, isTablet } from 'react-device-detect';
import { useTranslation } from "react-i18next";
import { AccessType } from "../../../common/enums/DRCXEnums";
import { UIRefreshCategoryEnum } from "../../../common/enums/UIRefreshCategoryEnum";
import { getAccessText } from "../../../common/utilities/GetAccessText";
import AccessLevelDialog, { bindAccessLevelDialog, useAccessLevelDialogState } from "../../../components/access-level-dialog/AccessLevelDialog";
import { ActionMenuItem, BaseActionEnum } from "../../../components/action-menu/ActionMenuItem";
import { ContentContainer } from "../../../components/content-container/ContentContainer";
import { ContentSection } from "../../../components/content-section/ContentSection";
import { ViewOptionStateEnum } from "../../../components/contextual-app-bar/ViewOptionStateEnum";
import DefaultConfigurationDialog, { bindDefaultConfigurationDialog, useDefaultConfigurationDialogState } from "../../../components/default-configuration-dialog/DefaultConfigurationDialog";
import LoadingZone from "../../../components/loading-zone/LoadingZone";
import NoData from "../../../components/no-data/NoData";
import OptionsSideSheet from "../../../components/options-side-sheet/OptionsSideSheet";
import RenameConfigurationDialog, { bindRenameConfigurationDialog, useRenameConfigurationDialogState } from "../../../components/rename-configuration-dialog/RenameConfigurationDialog";
import SideSheetFilterInput from "../../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput";
import SideSheetGroupedSelect, { SideSheetGroupedSelectOption } from "../../../components/side-sheet/side-sheet-grouped-select/SideSheetGroupedSelect";
import SummaryListingGrid from "../../../components/summary-listing-grid/SummaryListingGrid";
import { AppNotificationTypeEnum } from "../../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../../contexts/app-notification-context/UseAppNotificationContext";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../../contexts/offline-detect-context/UseOfflineDetectContext";
import { RestError } from "../../../services/rest-service/RestService";
import { NavigationSource } from "../../../common/enums/NavigationSource";
import { SummaryListingSortTypeEnum } from "../../../common/enums/SummaryListingSortTypeEnum";
import { UIViewEnum } from "../../../common/enums/UIViewEnum";
import { IeLynxObject } from "../../../common/models/eLynxObject";
import { useUserContext } from "../../../contexts/user-context/UserContext";
import { useUserFavoritesContext } from "../../../contexts/user-favorites-context/UserFavoritesContext";
import { useRouteContext } from "../../../hooks/use-route-context/UseRouteContext";
import { DEFAULT_REFRESH_SECONDS } from "../../../queries/BaseUseQueryParameters";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useWellSummaryConfigurations } from "../../../queries/group-summary/wells/UseWellSummaryConfigurations";
import { useWellSummaryData } from "../../../queries/group-summary/wells/UseWellSummaryData";
import { useObjectViewState } from "../../../queries/object-view-state/UseObjectViewState";
import { useDeletePageConfiguration } from "../../../queries/page-configuration/UseDeletePageConfiguration";
import { useModifyPageConfigurationAccess } from "../../../queries/page-configuration/UseModifyPageConfigurationAccess";
import { usePageConfiguration } from "../../../queries/page-configuration/UsePageConfiguration";
import { useRenamePageConfiguration } from "../../../queries/page-configuration/UseRenamePageConfiguration";
import { useSavePageConfigurationDefault } from "../../../queries/page-configuration/UseSavePageConfiguationDefault";
import { PageConfigurationAccessEnum, PageConfigurationTypeEnum } from "../../../services/page-configuration-service/Models";
import PageConfigurationService from "../../../services/page-configuration-service/PageConfigurationService";
import { IGroupViewState } from "../../../services/state-data-service/Models";
import { ISummaryListingExportParams } from "../../../services/summary-listing-data-service/BaseModels";
import { actionItems } from "../SummaryListingActions";
import { useSummaryListingContext } from "../SummaryListingContext";
import SummaryListingViewUtils, { ACCESS_LEVEL_MODIFY_CONFIRMATION_PARAMS, RENAME_CONFIGURATION_DIALOG_PARAMS, writePermission } from "../SummaryListingViewUtils";
import { postSortByOrder, postSortWellsOnAlarms } from "../common/Common";
import { PageConfigurationEditorDialogResult } from "../configuration-editor/PageConfigurationEditorDialog";
import { bindPageConfigurationEditorDialog, usePageConfigurationEditorDialogState } from "../configuration-editor/PageConfigurationEditorDialogState";
import useWellsViewState from "./WellsViewState";
import { createGridData, createViewOptions } from "./WellsViewUtils";
import { hasPermission } from "../../../common/utilities/HasPermission";
import { useEvaluateFeature } from "../../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../../services/features-data-service/Models";
import { useConfigurationsContext } from "../../../contexts/configurations-context/UseConfigurationsContext";
import { subscribe, unsubscribe } from "pubsub-js";
import { CONFIGURATION_CHANGED_TOPIC, ConfigurationChangedMsg, EXECUTE_ACTION_TOPIC } from "../../../common/models/pub-sub/PubSubTopics";
import _ from "lodash";
import SideSheetCheckbox from "../../side-sheet/side-sheet-checkbox/SideSheetCheckbox";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartGantt } from "@elynx/free-solid-svg-icons";
import { useUserSettings } from "../../../queries/user-settings/UseUserSettings";
import { RowDef } from "../../ag-grid-extensions/models/RowDef";
import React from "react";

const PageConfigurationEditorDialog = lazy(() => import("../configuration-editor/PageConfigurationEditorDialog"));

export type WellsViewProps = {
    visible?: boolean;
    setViewOptionState?: (state: ViewOptionStateEnum) => void;
};

export type WellsViewType = {
    executeAction: (action: BaseActionEnum | BaseActionEnum | string) => void;
    getExportParameters: () => ISummaryListingExportParams;
};

function WellsView({
    visible,
    setViewOptionState
}: WellsViewProps, ref: Ref<unknown> | undefined) {

    const [data, setData] = useState<{
            rows: RowDef[],
            cols: ColDef[]
        }[]>([]);

    const isPhone = isMobile && !isTablet;
    const { t } = useTranslation();
    const theme = useTheme();
    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const {/*state: summaryListingState,*/ setActions, setWellCount } = useSummaryListingContext();
    const {
        state,
        resetOptions,
        setConfigurationID,
        setFilterText,
        setDefaultConfigurationID,
        setNoData,
        setGridApi,
        setRefreshing,
        setFunctionIcons,
        setAlarmsAtTop
    } = useWellsViewState();

    const configurationDialog = usePageConfigurationEditorDialogState();   
    const { confirm, acknowledge} = useConfirmation(); 
    const renameConfigurationDialog = useRenameConfigurationDialogState();
    const defaultConfigurationDialog = useDefaultConfigurationDialogState();
    const accessLevelDialog = useAccessLevelDialogState();    
    const [, setAppNotificationState] = useAppNotificationContext();
    const [updateFavoriteContext] = useUserFavoritesContext();
    const {
        configurationsContextState, 
        setContextConfigurations,
        setContextConfigurationID
    } = useConfigurationsContext();


    useImperativeHandle<unknown, WellsViewType>(
        ref,
        () => ({
            executeAction: executeAction,
            getExportParameters: getExportParameters
        })
    );

    const { data: headerAndActionsV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.HeaderAndActionsV2,
    });

    const headerAndActionsV2 = (headerAndActionsV2Feature ?? false);
  
    const { data: objectViewState} = useQueryNotifications(useObjectViewState)({

        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject as IeLynxObject,
    });
    const groupViewState = objectViewState ? objectViewState as IGroupViewState : undefined;

    const {data: configurationModel/*, refetch*/} = useQueryNotifications(usePageConfiguration)({
        groupID: routeContext.groupID as number,
        configurationID: state.configurationID ?? -1,
        pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS_Well
    });

    const sortType: SummaryListingSortTypeEnum = groupViewState?.summaryListingSortType === SummaryListingSortTypeEnum.Custom && 
    configurationModel?.summaryListingSortType === SummaryListingSortTypeEnum.Custom ?
    SummaryListingSortTypeEnum.Custom : 
    SummaryListingSortTypeEnum.Alphanumeric;

    const { data: summaryConfigurations, refetch: refetchConfigurations } = useQueryNotifications(useWellSummaryConfigurations)({
        groupID: routeContext.groupID as number,
        enabled: true
    });

    const { data: summaryData, status, refetch } = useQueryNotifications(useWellSummaryData)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        offline: offlineState.offline,
        configurationID: state.configurationID,
        enabled: offlineState.offline || state.configurationID !== -1,
        refreshSeconds: userState.refreshIntervals.find(o => o.category === UIRefreshCategoryEnum.SummaryListing)?.refreshSeconds ?? DEFAULT_REFRESH_SECONDS,
        onSuccess: () => {
            setTimeout(() => setRefreshing(false), 2000);
        },
        onError: () => {
            setRefreshing(false);
        }

    });

    const { mutate: renameConfiguration } = useQueryNotifications(useRenamePageConfiguration)({
        groupID: routeContext.groupID as number,
        configurationID: state.configurationID,
        onSuccess: (data) => {
            acknowledge(t('configuration.renameSuccessMessage', {
                configurationType: t("configuration.types.pageConfiguration"),
                oldName: data.oldName,
                newName: data.newName
            }));
        }
    });

    const { mutate: deleteConfiguration } = useQueryNotifications(useDeletePageConfiguration)({
        groupID: routeContext.groupID as number,
        //configurationID: state.configurationID as number,
        //pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS_Well,
        onSuccess: (data) => {

            acknowledge(t('configuration.deleteSuccessMessage', {
                configurationType: t("configuration.types.pageConfiguration"),
                configurationName: data.deletedName
            }));

            setConfigurationID(summaryConfigurations?.defaultPageConfigurationId ?? -1);
        }
    });

    const { mutate: setDefaultConfiguration } = useQueryNotifications(useSavePageConfigurationDefault)({
        groupID: routeContext.groupID as number,
        pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS_Well,
        onSuccess: (data) => {

            setDefaultConfigurationID(data.configurationID as number);

            let message = '';

            if (data.defaultForUser && data.defaultForAllUsers) {
                message = t('defaultConfiguration.success1');
            } else if (data.defaultForUser && data.defaultForGroup) {
                message = t('defaultConfiguration.success2');
            } else if (data.defaultForAllUsers) {
                message = t('defaultConfiguration.success3');
            } else if (data.defaultForGroup) {
                message = t('defaultConfiguration.success4');
            } else {
                message = t('defaultConfiguration.success5');
            }

            acknowledge(message);
        },
    });

    const { mutate: modifyAccess } = useQueryNotifications(useModifyPageConfigurationAccess)({
        groupID: routeContext.groupID as number,
        configurationID: state.configurationID as number,
        onSuccess: (data) => {
            acknowledge(t("configuration.accessLevelModifySuccessMessage", {
                configurationType: t("configuration.types.pageConfiguration"),
                oldAccess: getAccessText(data.oldAccess),
                newAccess: getAccessText(data.newAccess)
            }));
        }
    });

    const { data: userSettings } = useQueryNotifications(useUserSettings)({
        enabled: !offlineState.offline
    });




    const noDataMessage = t("summaryListing.nodata", { objectType: 'wells' });
    const noDataFilterMessage = t("summaryListing.nodataFilter", { objectType: 'wells' });
    const defaultConfigID = useMemo(() => {

        return summaryConfigurations?.defaultPageConfigurationId ?? -1;
    }, [
        summaryConfigurations?.defaultPageConfigurationId
    ]);

    const sharedConfigurations = useMemo(() => {
        return summaryConfigurations && !offlineState.offline
        ? createViewOptions(summaryConfigurations?.sharedConfigurations,
            PageConfigurationAccessEnum.Shared,
            defaultConfigID)
        : new Array<SideSheetGroupedSelectOption>();
    }, [
        defaultConfigID,
        summaryConfigurations,
        offlineState
    ]);

    const sharedReadOnlyConfigurations = useMemo(() => {
        return summaryConfigurations && !offlineState.offline
        ? createViewOptions(summaryConfigurations?.sharedConfigurations,
            PageConfigurationAccessEnum.SharedReadOnly,
            defaultConfigID)
        : new Array<SideSheetGroupedSelectOption>();
    }, [
        defaultConfigID,
        summaryConfigurations,
        offlineState
    ]);

    const privateConfigurations = useMemo(() => { 
        return summaryConfigurations && !offlineState.offline
        ? createViewOptions(summaryConfigurations?.privateConfigurations,
            PageConfigurationAccessEnum.Private,
            defaultConfigID)
        : new Array<SideSheetGroupedSelectOption>();
    }, [
        defaultConfigID,
        summaryConfigurations,
        offlineState
    ]);


    // const data = createGridData(
    //     summaryData,
    //     isPhone,
    //     routeContext.groupID as number,
    //     isPhone ? false : state.summaryListingDisplayFunctionIcons,
    //     sortType
    // );

    async function onCreateConfiguration() {
        handleEditorResult(await configurationDialog.open({
            pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS_Well
        }));
    }

    async function onDuplicateConfiguration() {
        handleEditorResult(await configurationDialog.open({
            configurationID: state.configurationID ?? -1,
            pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS_Well,
            duplicate: true
        }));
    }


    function handleEditorResult(result: PageConfigurationEditorDialogResult) {
        if (result.reason === "saveAsNew" || result.reason === "saveChanges") {
            setConfigurationID(result.values?.configurationID as number);
            refetchConfigurations();
            refetch();
        }
    }

    async function onEditConfiguration() {

        // if the currently selected configuration is shared or shared readonly, then
        // display a dialog warning that changes will affect other users
        if (state.configurationID) {
            const configuration = summaryConfigurations?.sharedConfigurations?.find(o => o.id === state.configurationID);

            if (configuration) {
                const result = await confirm({
                    dialogTitle: t("configuration.editOrDuplicateConfirmation.title"),
                    confirmationText: t("configuration.editOrDuplicateConfirmation.confirmationText", { configurationType: t("configuration.types.pageConfiguration") }),
                    yesButtonText: t("configuration.editOrDuplicateConfirmation.yesButtonText"),
                    noButtonText: t("configuration.editOrDuplicateConfirmation.noButtonText"),
                    showConfirmationText: true,
                    showYesButton: true,
                    showNoButton: true,
                    showCloseButton: true
                });

                if (result === "yes") {

                    // duplicate
                    //await duplicateConfiguration();
                } else if (result === "no") {

                    // continue editing
                    handleEditorResult(await configurationDialog.open({
                        configurationID: state.configurationID ?? -1,
                        pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS_Well
                    }));

                } // else do nothing
            } else {

                handleEditorResult(await configurationDialog.open({
                    configurationID: state.configurationID ?? -1,
                    pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS_Well
                }));
            }
        }
    }

    const onRenameConfiguration = async () => {

        const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                                .find(o => o.id === state.configurationID);

        if (configuration?.access) {

            if (configuration?.access === PageConfigurationAccessEnum.Shared ||
                configuration?.access === PageConfigurationAccessEnum.SharedReadOnly) {

                const response = await confirm(RENAME_CONFIGURATION_DIALOG_PARAMS);
                if (response !== "yes") {
                    return;
                }
            }

            const result = await renameConfigurationDialog.open({
                configurationName: configuration?.name as string,
                configurationType: t("configuration.types.pageConfiguration")
            });

            if (result.reason === 'ok' && result.values) {

                try {

                    renameConfiguration(result.values.configurationName);

                } catch (error) {

                    const err = error as RestError;

                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        message: err.message,
                        show: true,
                        error: err
                    });
                }
            }
        }
    };

    const onDeleteConfiguration = async () => {

        const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                                .find(o => o.id === state.configurationID);

        if (configuration) {
            const response = await confirm({
                dialogTitle: t('configuration.deleteConfirmation.title', {
                    configurationType: t("configuration.types.pageConfiguration"),
                    configurationName: configuration.name
                }),
                confirmationText: t('configuration.deleteConfirmation.confirmationText'),
                yesButtonText: t("configuration.deleteConfirmation.yesButtonText"),
                noButtonText: t("configuration.deleteConfirmation.noButtonText"),
                showConfirmationText: true,
                showYesButton: true,
                showNoButton: true
            });


            if (response === "yes") {

                try {

                    deleteConfiguration({
                        configurationID: state.configurationID as number,
                        pageConfigurationType: PageConfigurationTypeEnum.SummaryListing_RTS,
                    });

                } catch (error) {

                    const err = error as RestError;

                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        message: err.message,
                        show: true,
                        error: err
                    });
                }
            }
        }
    };
    

    const onDefaultConfiguration = async () => {


        const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                                .find(o => o.id === state.configurationID);

        if (configuration) {

            const hasWritePermission = hasPermission(groupViewState?.accessPermissions ?? [], writePermission);

            try {

                if (configuration.access === PageConfigurationAccessEnum.Private || !hasWritePermission) {

                    setDefaultConfiguration({
                        configurationID: configuration.id,
                        defaultForUser: true,
                        defaultForGroup: false,
                        defaultForAllUsers: false
                    });


                } else {
                    const defaultOptions = await PageConfigurationService.getPageConfigurationDefaultOptions(routeContext.groupID as number, configuration.id as number);

                    const result = await defaultConfigurationDialog.open({
                        configurationName: configuration.name ?? '',
                        groupName: summaryConfigurations?.groupDescription ?? '',
                        forCurrentUser: defaultOptions.defaultForUser ?? false,
                        forUsersWithoutDefault: defaultOptions.defaultForGroup ?? false,
                        forAllUsers: defaultOptions.defaultForAllUsers ?? false,
                        configurationID: configuration.id as number
                    });

                    if (result.reason === 'ok' && result.values) {
                        setDefaultConfiguration({
                            configurationID: configuration.id,
                            defaultForUser: result.values.forCurrentUser,
                            defaultForGroup: result.values.forUsersWithoutDefault,
                            defaultForAllUsers: result.values.forAllUsers
                        });
                    }
                }
            } catch (error) {

                const err = error as RestError;

                setAppNotificationState({
                    notificationType: AppNotificationTypeEnum.Error,
                    message: err.message,
                    show: true,
                    error: err
                });
            }
        }
    };

    async function onModifyPermissions() {


        const configuration = (summaryConfigurations?.privateConfigurations ?? [])
                                .concat(summaryConfigurations?.sharedConfigurations ?? [])
                                .find(o => o.id === state.configurationID);

        if (configuration) {

        const userHasWriteAccessToConfiguration = ((configuration?.access === PageConfigurationAccessEnum.Private) ||
             configuration?.userHasPermission(writePermission)) ?? false;


            const userHasModifyPermission = configuration?.userHasPermission(writePermission); 
            
            if (configuration?.access === PageConfigurationAccessEnum.Shared ||
                configuration?.access === PageConfigurationAccessEnum.SharedReadOnly) {

                const response = await confirm(ACCESS_LEVEL_MODIFY_CONFIRMATION_PARAMS);
                if (response !== "yes") { 
                    return;
                }
            }

            const result = await accessLevelDialog.open({
                access: configuration?.access === PageConfigurationAccessEnum.Private ? 
                                        AccessType.Private : 
                                        configuration?.access === PageConfigurationAccessEnum.Shared ?
                                        AccessType.Shared : 
                                        configuration?.access === PageConfigurationAccessEnum.SharedReadOnly ?
                                        AccessType.SharedReadOnly : 
                                        AccessType.None,
                configurationType: t("configuration.types.pageConfiguration"),
                hasGroupWrite: userHasModifyPermission,
                hasConfigWrite: userHasWriteAccessToConfiguration
            });

            if (result.reason === 'ok' && result.values?.access) {

                try {

                    modifyAccess(result.values.access);

                } catch (error) {

                    const err = error as RestError;

                    setAppNotificationState({
                        notificationType: AppNotificationTypeEnum.Error,
                        message: err.message,
                        show: true,
                        error: err
                    });
                }
            }
        }

    }

    function executeAction(action: BaseActionEnum | BaseActionEnum | string) {
        switch (action) {
            case BaseActionEnum.Refresh:
                setRefreshing(true);
                refetch();
                break;

            case BaseActionEnum.Edit:
                onEditConfiguration();
                break;

            case BaseActionEnum.SetAsDefault:
                onDefaultConfiguration();
                break;

            case BaseActionEnum.CreateNew:
                onCreateConfiguration();
                break;

            case BaseActionEnum.Duplicate:
                onDuplicateConfiguration();
                break;

            case BaseActionEnum.ModifyPermissions:
                onModifyPermissions();
                break;

            case BaseActionEnum.Delete:
                onDeleteConfiguration();
                break;

            case BaseActionEnum.Rename:
                onRenameConfiguration();
                break;
    
                
        }
    }

    const handleViewChange = (value: number) => {
        setConfigurationID(value ?? state.configurationID);
    };

    // Don't want to do this. This hides the grid when column filters are applied
    // that result in to data. If the grid is hidden, the user can't see the filters
    // anymore.
    // function onFilterChanged(event: FilterChangedEvent) {
    //     setNoData(event.api.getRenderedNodes().length === 0);
    // }

    function getExportParameters() {

        const sortedColumn = state.gridApi?.getColumnState().find(o => o.sort);

        const index = sortedColumn?.sortIndex ?? -1;

        const isSortAscending = sortedColumn
            ? state.gridApi?.getColumn(sortedColumn?.colId)?.getSort() === 'asc'
            : false;

        return {

            configurationID: state.configurationID,
            objectIDs: summaryData?.gridData?.rows.map(o => o.wellID),
            sortByRecentMemo: index === 0,
            sortAscending: isSortAscending ?? false,
            sortColumnIndex: state.summaryListingDisplayFunctionIcons ? 
                            index > 1 ? 
                                index - 2 : 
                                0 :
                            index > 0 ? 
                                index - 1 :
                                0,
            useCustomOrdering: sortType === SummaryListingSortTypeEnum.Custom,
        } as ISummaryListingExportParams;
    }



    function onResetOptions() {
        resetOptions();
    }

    function onGridReady(event: AgGridEvent) {
        setGridApi(event.api);
    }

    function onDisplayShortcuts(checked: boolean) {

        setFunctionIcons(checked);

        // the grid data must be cleared to cause the ag grid to correctly render the columns        
        setData([]);
    }

    function onDisplayAlarmsFirst(checked: boolean) {
        setAlarmsAtTop(checked);
    }

    useEffect(() => {

        const griddata = createGridData(
            summaryData,
            isPhone,
            routeContext.groupID as number,
            isPhone ? false : state.summaryListingDisplayFunctionIcons,
            sortType
        );

        setData([griddata]);


    }, [
        summaryData,
        isPhone,
        routeContext.groupID,
        state.summaryListingDisplayFunctionIcons,
        sortType,
        setData,
    ]);


    useEffect(() => {
        const rowCount = summaryData?.gridData?.rows.length ?? 0;
        setWellCount(rowCount);
        setNoData(rowCount === 0);
    }, [summaryData, setWellCount, setNoData]);

    useEffect(() => {
        if (visible) {
            const viewOptionState = state.isOptionsDirty ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            setViewOptionState && setViewOptionState(viewOptionState);
        }
    }, [
        visible,
        state.isOptionsDirty,
        setViewOptionState
    ]);

    // get progress running for current object
    //const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    // useEffect(() => {

    //     if (visible) {

    //         const items = offlineState.offline ? 
    //                         [] :
    //                         [...actionItems].filter(o => o.action === BaseActionEnum.Refresh || o.action === BaseActionEnum.Export);

    //         const exportAction = items.find(o => o.action === BaseActionEnum.Export); 

    //         if (exportAction) {
    //             exportAction.disabled = (progress && progress.uid === state.downloadUid) || (summaryData?.gridData?.rows?.length ?? 0) === 0
    //         }

    //         if (!_.isEqual(items, summaryListingState.actions)) {
    //             setActions(items);
    //         }
    //     }
    // }, [
    //     visible,
    //     progress,
    //     setActions,
    //     state.downloadUid,
    //     summaryData,
    //     offlineState.offline,
    //     summaryListingState.actions
    // ]);

    useEffect(() => {

        if (visible) {

            let actions: ActionMenuItem[] = [];

            if (summaryConfigurations) {
                actions = headerAndActionsV2 ?
                SummaryListingViewUtils.getActionItemsV2(actionItems,
                    isPhone,
                    offlineState.offline,
                    state.configurationID,
                    summaryConfigurations
                )
                :
                SummaryListingViewUtils.getActionItems(actionItems,
                    isPhone,
                    offlineState.offline,
                    state.configurationID,
                    summaryConfigurations
                );
            }


            setActions(actions);
        }

    }, [
        headerAndActionsV2,
        visible,
        state.configurationID,
        isPhone,
        offlineState.offline,
        summaryConfigurations,
        setActions,
        userState.user?.loginName // temporary
    ]);

    // Update the configuration id
    useEffect(() => {
        if (!offlineState.offline) {
            
            setDefaultConfigurationID(defaultConfigID);

            const configID = (routeContext.viewID === UIViewEnum.Group_SummaryListing_Wells ? routeContext.state?.configurationID : undefined);
            const navSrc = routeContext.navSrc;

            if (configID) {
                setConfigurationID(configID);
            } else if (navSrc !== NavigationSource.Tab && defaultConfigID > 0) {
                setConfigurationID(defaultConfigID);
            }
            else if (navSrc === NavigationSource.Tab) {
                setConfigurationID(cid => cid > -1 ? cid : defaultConfigID);
            }
        }
    }, [
        defaultConfigID,
        // This does not work correctly. Need entire routeContext.
        // routeContext.viewID,
        // routeContext.state?.configurationID,
        // routeContext.navSrc,
        routeContext,
        offlineState.offline,
        setDefaultConfigurationID,
        setConfigurationID
    ]);

    // Keep the favorite context in sync with the selected configuration
    useEffect(() => {

        if (routeContext.viewID === UIViewEnum.Group_SummaryListing_Wells) {

            let configuration = summaryConfigurations?.privateConfigurations?.find(c => c.id === state.configurationID);

            if (!configuration) {
                configuration = summaryConfigurations?.sharedConfigurations?.find(c => c.id === state.configurationID);
            }

            if(configuration) {
                // console.log("#### Summary Listing - Wells updateFavoriteContext");

                updateFavoriteContext({
                    // Only include configuration id and name if something other than the default is selected
                    configurationID: summaryConfigurations?.defaultPageConfigurationId !== state.configurationID
                        ? state.configurationID > -1 ? state.configurationID : undefined
                        : undefined,
                    configurationName: summaryConfigurations?.defaultPageConfigurationId !== state.configurationID
                        ? configuration?.name
                        : undefined
                });
            }
        }
    }, [
        state.configurationID,
        summaryConfigurations?.privateConfigurations,
        summaryConfigurations?.sharedConfigurations,
        summaryConfigurations?.defaultPageConfigurationId,
        routeContext.viewID,
        updateFavoriteContext
    ]);

    useEffect(() => {
        if (userSettings) {
            setAlarmsAtTop(userSettings.summaryListingAlarmAtTop);
            setFunctionIcons(userSettings.summaryListingDisplayFunctionIcons);
        }

    },[
        userSettings,
        setAlarmsAtTop,
        setFunctionIcons
    ]);

    useEffect(() => {

        if (visible && headerAndActionsV2) {

            const groups= [{
                    heading: t("summaryListing.viewList.sharedViewsHeading"),
                    options: sharedConfigurations
                }, {
                    heading: t("summaryListing.viewList.sharedReadOnlyViewsHeading"),
                    options: sharedReadOnlyConfigurations

                }, {
                    heading: t("summaryListing.viewList.privateViewsHeading"),
                    options: privateConfigurations
            }];

            if (!_.isEqual(configurationsContextState.configurations, groups)) {

                setContextConfigurations(groups);
            }
        }
    }, [
        visible,
        headerAndActionsV2,
        sharedConfigurations,
        sharedReadOnlyConfigurations,
        privateConfigurations,
        t,
        setContextConfigurations,
        configurationsContextState.configurations
    ]);

    useEffect(() => {
        if (visible && headerAndActionsV2) {
            setContextConfigurationID(state.configurationID);
        }

    }, [
        visible,
        headerAndActionsV2,
        state.configurationID,
        setContextConfigurationID
    ]);


    useEffect(() => {

        if (visible) {
            const token = subscribe(CONFIGURATION_CHANGED_TOPIC, (message, data) => setConfigurationID((data as ConfigurationChangedMsg).configurationID));
    
            return () => {
                if (token) {
                    unsubscribe(token);
                }
            }
        }
    }, [
        visible,
        setConfigurationID
    ]);

    return (
        <>
            {visible &&
                <Box sx={{
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                    overflow: "hidden",
                }}>

                    {!offlineState.offline &&
                    <>
                        <PageConfigurationEditorDialog {...bindPageConfigurationEditorDialog(configurationDialog)} />                        
                        <RenameConfigurationDialog {...bindRenameConfigurationDialog(renameConfigurationDialog)} /> 
                        <DefaultConfigurationDialog {...bindDefaultConfigurationDialog(defaultConfigurationDialog)} /> 
                        <AccessLevelDialog {...bindAccessLevelDialog(accessLevelDialog)} />
                    </>
                        
                    }

                    <LoadingZone isLoading={status === "loading" || state.refreshing}>
                        <ContentContainer>
                            <ContentSection>
                                {data.map((o, ndx) => (
                                    <React.Fragment key={ndx}>
                                        <SummaryListingGrid
                                            show={state.isNoData === false}
                                            gridData={o}
                                            showFooter={true}
                                            filterText={state.filterText}
                                            postSort={sortType === SummaryListingSortTypeEnum.Custom ? 
                                                postSortByOrder :
                                                state.summaryListingAlarmAtTop
                                                ? postSortWellsOnAlarms
                                                : undefined
                                            }
                                            // onFilterChanged={onFilterChanged}
                                            onGridReady={onGridReady}                                    
                                            getRowId={data => data.data.wellID.toString()}
                                        />

                                        {state.isNoData && (status === "success" || status === "error") &&
                                            <NoData text={o.rows.length === 0 ? noDataMessage : noDataFilterMessage} />
                                        }
                                    </React.Fragment>

                                ))}
                            </ContentSection>

                            <OptionsSideSheet onReset={onResetOptions}>

                                {!offlineState.offline && (!headerAndActionsV2 || isPhone) &&
                                    <SideSheetGroupedSelect
                                        value={state.configurationID}
                                        label={t("summaryListing.viewList.placeholder")}
                                        optionGroups={[{
                                            heading: t("summaryListing.viewList.sharedViewsHeading"),
                                            options: sharedConfigurations
                                        }, {
                                            heading: t("summaryListing.viewList.sharedReadOnlyViewsHeading"),
                                            options: sharedReadOnlyConfigurations

                                        }, {
                                            heading: t("summaryListing.viewList.privateViewsHeading"),
                                            options: privateConfigurations
                                        }]}
                                        onChange={handleViewChange} />
                                }

                                <SideSheetFilterInput
                                    label="Find in Well List"
                                    value={state.filterText}
                                    onChange={(value) => {
                                        setFilterText(value);
                                    }}
                                />

                                {headerAndActionsV2 ? 
                                <>

                                <Typography sx={{
                                    fontWeight: 600
                                }}>
                                    Display
                                </Typography>


                                {
                                    !isPhone && 
                                    <SideSheetCheckbox
                                        //size="small"
                                        //name="functionIcons"
                                        label={<Stack direction='row' sx={{
                                            color: theme.palette.text.secondary
                                        }}>
                                            <Typography>{t('profile.shortcuts')}</Typography>
                                            <FontAwesomeIcon icon={faChartGantt}  style={{  marginLeft: "8px", height: "20px", width: "20px" }}/>
                                            <i className='sw sw-money-graph fa-lg half-right' style={{ marginLeft: "8px", height: '20px', width: '20px' }} />
                                            <i className='sw sw-tank fa-lg half-right' style={{ marginLeft: "8px", height: '20px', width: '20px' }} /> 
                                        </Stack>}
                                        checked={state.summaryListingDisplayFunctionIcons}
                                        onChange={(checked) => onDisplayShortcuts(checked)}
                                    />
                                }
                                    <SideSheetCheckbox
                                        //name="alarmsAtTop"
                                        sx={{color: theme.palette.text.secondary}}
                                        label={t('profile.alarmFirst', {objectType: "Devices"})}
                                        checked={state.summaryListingAlarmAtTop}
                                        onChange={(checked) => onDisplayAlarmsFirst(checked)}
                                    />
                                    </>
                                    : 
                                    null
                                }

                            </OptionsSideSheet>
                        </ContentContainer>
                    </LoadingZone>
                </Box>
            }
        </>
    );
}

export default forwardRef(WellsView);